import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import type { CreditActionsResponse, WithdrawDisabledReason } from '@/api/credit/types';
import { ProductNames } from '@/constants/ProductNames';
import useFeatureFlag from '@/featureFlags/useFeatureFlag';
import { creditFeaturesQueryOptions } from '@/features/products/queries/featuresQuery/featuresQuery';
import { selectWithdrawData } from '@/features/products/queries/featuresQuery/featuresQuerySelectors';
import useModal from '@/hooks/useModal/useModal';
import type {
  WithdrawAccessReturn,
  WithdrawNavigationOptions,
} from '@/pages/Credit/hooks/useWithdrawAccessGeneric/types';
import { creditActionsQueryOptions } from '@/queries/creditActionsQuery/creditActionsQuery';
import { selectWithdrawAction } from '@/queries/creditActionsQuery/creditActionsQuerySelectors';
import { meQueryOptions } from '@/queries/meQuery/meQuery';
import { createActivatedProductSelector } from '@/queries/meQuery/meQuerySelectors';
import { routeMap } from '@/routes/routeMap';
type WithdrawAccessGenericReturn = WithdrawAccessReturn & {
  blockedReasons: WithdrawDisabledReason[];
};

const useWithdrawAccessGeneric = (): WithdrawAccessGenericReturn => {
  const { openModal, closeModal } = useModal();
  const navigate = useNavigate();
  const isEmailVerificationEnabled = useFeatureFlag('emailVerificationNeededForWithdraw');

  const selectWithdrawActionWithEmailFilter = useCallback(
    (creditActions: CreditActionsResponse): CreditActionsResponse['withdraw'] => {
      const withdraw = selectWithdrawAction(creditActions);
      if (
        !withdraw.disabled ||
        isEmailVerificationEnabled ||
        !withdraw.reasons.includes('EmailVerificationNeeded')
      ) {
        return withdraw;
      }

      const filteredReasons = withdraw.reasons.filter(reason => reason !== 'EmailVerificationNeeded');
      if (filteredReasons.length === 0) {
        return { disabled: false };
      }
      return { disabled: true, reasons: filteredReasons };
    },
    [isEmailVerificationEnabled],
  );

  const { data: withdrawAction, isPending: isWithdrawActionPending } = useQuery({
    ...creditActionsQueryOptions(),
    select: selectWithdrawActionWithEmailFilter,
  });
  const { data: creditProduct, isPending: isCreditProductPending } = useQuery({
    ...meQueryOptions(),
    select: createActivatedProductSelector(ProductNames.CREDIT),
  });
  const { data: withdrawData, isPending: isWithdrawDataPending } = useQuery({
    ...creditFeaturesQueryOptions(),
    select: selectWithdrawData,
  });

  const isWithdrawStepsEmpty = withdrawData?.features.length === 0;
  const blockedReasons = withdrawAction?.disabled ? withdrawAction.reasons : [];
  const hasReachedCreditLimit = creditProduct?.amountUsed === creditProduct?.creditLimit;
  const hasModalBlockedReason = blockedReasons.length > 0;
  const isWithdrawActionHardDisabled = !!withdrawAction?.disabled && !hasModalBlockedReason;

  const isLoading = isWithdrawActionPending || isCreditProductPending || isWithdrawDataPending;
  const isDisabled = hasReachedCreditLimit || isWithdrawActionHardDisabled;
  const isBlocked = hasModalBlockedReason || isWithdrawStepsEmpty;

  const attemptWithdrawNavigation = useCallback(
    (options?: WithdrawNavigationOptions) => {
      if (isWithdrawStepsEmpty) {
        openModal('WITHDRAWAL_NOT_POSSIBLE', { confirmAction: closeModal });
        return;
      }

      void navigate(routeMap.credit.withdrawal, options?.replace ? { replace: true } : undefined);
    },
    [closeModal, isWithdrawStepsEmpty, navigate, openModal],
  );

  const resolveStatus = () => {
    if (isLoading) {
      return 'loading';
    }
    if (isDisabled) {
      return 'disabled';
    }
    if (isBlocked) {
      return 'blocked';
    }
    return 'ready';
  };

  return { status: resolveStatus(), blockedReasons, attemptWithdrawNavigation };
};

export default useWithdrawAccessGeneric;
